import React from "react";
import Add from "@mui/icons-material/Add";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { CardLoading } from "../core/CardLoading";
import { EditableListItemUrl } from "../core/EditableListItemUrl";
import { enqueueSnackbar } from "notistack";
import { useDeleteLink, useLinksForGame, useUpdateLink } from "@seabrookstudios/pitch2table";
import AddGameLinkFullScreenDialog from "./AddGameLinkFullScreenDialog";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { getBrandIcon } from "../core/linked-service-icons";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").Links} props.link
 */
const EditableLink = ({ gameId, link }) => {
  const onUpdateSuccess = () => {
    enqueueSnackbar("Link updated successfully", { variant: "success" });
  };

  const onUpdateError = () => {
    enqueueSnackbar("Failed to update link", { variant: "error" });
  };

  const onDeleteSuccess = () => {
    enqueueSnackbar("Link deleted successfully", { variant: "success" });
  };

  const onDeleteError = () => {
    enqueueSnackbar("Failed to delete link", { variant: "error" });
  };

  const updateGameLink = useUpdateLink(gameId, link.id, onUpdateSuccess, onUpdateError);
  const deleteGameLink = useDeleteLink(gameId, link.id, onDeleteSuccess, onDeleteError);

  /**
   * @param {import("@seabrookstudios/types").Url} url
   */
  const handleUpdateUrl = async (url) => {
    await updateGameLink.mutateAsync({ url, name: undefined });
  };

  const handleDeleteLink = async () => {
    await deleteGameLink.mutateAsync();
  };

  const icon = getBrandIcon(link.url);

  return (
    <EditableListItemUrl
      key={link.id}
      title={link.name}
      text={link.url}
      Icon={
        icon
          ? () => (
              <img
                alt={`logo for ${icon.title}`}
                height="20"
                src={icon.fullPath ? icon.slug : `https://cdn.simpleicons.org/${icon.slug}?viewbox=auto`}
              />
            )
          : undefined
      }
      helperText="Edit the URL for this link"
      onSave={handleUpdateUrl}
      onDelete={handleDeleteLink}
      isBusy={updateGameLink.isPending}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const GameLinksCard = ({ gameId }) => {
  const { data: links = [], isLoading } = useLinksForGame(gameId);

  if (isLoading) {
    return <CardLoading />;
  }

  return (
    <Card elevation={1} square={false}>
      <List>
        <ListItem>
          <ListItemText primary="Links" />
          <ListItemSecondaryAction>
            <AddGameLinkFullScreenDialog
              gameId={gameId}
              CustomControl={
                <IconButton color="primary" size="small">
                  <Add />
                </IconButton>
              }
            />
          </ListItemSecondaryAction>
        </ListItem>

        {links.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No links added yet"
              secondary="Add links to external resources like sheets, shared docs or virtual play testing platforms"
            />
          </ListItem>
        ) : (
          links.map((link) => <EditableLink gameId={gameId} link={link} key={link.id} />)
        )}
      </List>
    </Card>
  );
};
